import type { DynamicClient } from '@dynamic-labs-sdk/client';
import {
  assertDefined,
  getVerifiedCredentialForWalletAccount,
} from '@dynamic-labs-sdk/client/core';
import type { EvmWalletAccount } from '@dynamic-labs-sdk/evm';
import { createEcdsaKernelMigrationAccount } from '@zerodev/ecdsa-validator';
import {
  type CreateKernelAccountReturnType,
  constants,
  createKernelAccount as createZerodevKernelAccount,
} from '@zerodev/sdk';
import type { PublicClient } from 'viem';
import type { EntryPointVersion } from 'viem/account-abstraction';
import type { SignAuthorizationReturnType } from 'viem/accounts';

import { getSignerForSmartWalletAccount } from '../../getSignerForSmartWalletAccount';
import { getEcdsaValidator } from '../getEcdsaValidator';
import { getEntryPoint } from '../getEntryPoint';
import { getKernelVersion } from '../getKernelVersion';
import { getZerodevProviderFromSettings } from '../getZerodevProviderFromSettings';
import { shouldUseEIP7702 } from '../shouldUseEIP7702';

type CreateKernelAccountParams = {
  eip7702Auth?: SignAuthorizationReturnType;
  publicClient: PublicClient;
  smartWalletAccount: EvmWalletAccount;
};

/**
 * Creates a ZeroDev kernel account for a given smart wallet account.
 * This is a lower-level function used internally by createKernelClientForWalletAccount.
 *
 * @param params - The parameters for creating the kernel account.
 * @param params.publicClient - The viem public client to use for blockchain interactions.
 * @param params.smartWalletAccount - The EVM smart wallet account to create the kernel account for.
 * @param [params.eip7702Auth] - A pre-signed EIP-7702 authorization. When provided, this authorization is passed to the ZeroDev SDK instead of signing a new one internally. Useful for singleUse MFA flows.
 * @param client - The Dynamic client instance.
 * @returns A promise that resolves to a ZeroDev kernel account.
 */
export const createKernelAccount = async (
  { publicClient, smartWalletAccount, eip7702Auth }: CreateKernelAccountParams,
  client: DynamicClient
): Promise<CreateKernelAccountReturnType<EntryPointVersion>> => {
  const zerodevProvider = getZerodevProviderFromSettings(client);

  assertDefined(
    zerodevProvider,
    'Zerodev provider is not enabled in project settings'
  );

  const signer = await getSignerForSmartWalletAccount(
    {
      smartWalletAccount,
    },
    client
  );

  const walletProperties = getVerifiedCredentialForWalletAccount(
    { walletAccount: smartWalletAccount },
    client
  )?.walletProperties;

  const entryPointVersion =
    walletProperties?.entryPointVersion ?? zerodevProvider.entryPointVersion;
  const entryPoint = getEntryPoint(entryPointVersion);

  const useEIP7702 = shouldUseEIP7702({ smartWalletAccount }, client);

  if (useEIP7702) {
    return createZerodevKernelAccount(publicClient, {
      eip7702Account: signer,
      eip7702Auth,
      entryPoint,
      kernelVersion: constants.KERNEL_V3_3,
    });
  }

  const kernelVersionValue =
    walletProperties?.kernelVersion ?? zerodevProvider.kernelVersion;

  const kernelVersion = getKernelVersion({
    entryPoint,
    kernelVersion: kernelVersionValue,
  });

  const kernelV3MigrationEnabled =
    zerodevProvider.enableKernelV3Migration ?? false;

  if (kernelV3MigrationEnabled) {
    const apiKernelVersion = getKernelVersion({
      entryPoint,
      kernelVersion: zerodevProvider.kernelVersion,
    });

    return createEcdsaKernelMigrationAccount(publicClient, {
      entryPoint,
      migrationVersion: {
        from: kernelVersion,
        to: apiKernelVersion,
      },
      signer,
    });
  }

  const validator = await getEcdsaValidator({
    ecdsaProviderType:
      walletProperties?.ecdsaProviderType ?? zerodevProvider.ecdsaProviderType,
    entryPoint,
    kernelVersion,
    publicClient,
    signer,
  });

  return createZerodevKernelAccount(publicClient, {
    entryPoint,
    kernelVersion,
    plugins: {
      sudo: validator,
    },
  });
};
